/**
 * PayrollMassPlanPrint — page « Masse salariale planifiée » du PDF Planification.
 * Brut planifié (contrats + mouvements) × taux → net / coût employeur, par mois.
 * Réplique l'onglet Masse salariale planifiée (même plan, CALCULÉ PAR LE SERVEUR).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../../i18n/useP';
import { getSocialChargesPlanPeople } from '../../../api/socialChargesPlan';
import { formatNumber } from '../../../utils/format';
import PrintReportHeader from './PrintReportHeader';

const fmtChf = (n) => formatNumber(Math.round(Number(n) || 0), 0);
const sum = (a) => a.reduce((x, y) => x + y, 0);

export default function PayrollMassPlanPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const N = year || new Date().getFullYear();
  /* Plan CALCULÉ PAR LE SERVEUR (moteur migré du JavaScript le 2026-08-21). */
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!tenantId) return;
    getSocialChargesPlanPeople(tenantId, N).then((r) => {
      setPlan(r.error ? null : r.data);
      onDataReady && onDataReady('payroll-mass');
    }).catch(() => {});
  }, [tenantId, N]); // eslint-disable-line

  if (!plan) return null;
  const S = (a) => (a || []).slice(0, 12);
  const months = plan.months.slice(0, 12);
  const gross = S(plan.gross);
  const net = S(plan.net);
  const cout = S(plan.cout);
  const sub = S(plan.sub);
  const chargesSalarie = gross.map((g, i) => g - net[i]);
  const chargesEmployeur = cout.map((c, i) => c - gross[i]);
  const charges = (plan.charges || []).map((c) => ({
    ...c, empl: S(c.empl), empr: S(c.empr),
    hasEmpl: S(c.empl).some((v) => Math.round(v) !== 0),
    hasEmpr: S(c.empr).some((v) => Math.round(v) !== 0),
  }));

  const Row = ({ label, arr, strong }) => (
    <tr className={strong ? 'ppl-print__row--strong' : ''}>
      <td className={strong ? '' : ''}>{strong ? <strong>{label}</strong> : label}</td>
      {arr.map((v, i) => <td key={i} className="num nowrap">{Math.round(v) === 0 ? '—' : fmtChf(v)}</td>)}
      <td className="num nowrap"><strong>{fmtChf(sum(arr))}</strong></td>
    </tr>
  );
  const SecRow = ({ label }) => (
    <tr><td colSpan={14} style={{ background: '#F1F5FB', fontWeight: 600 }}>{label}</td></tr>
  );

  return (
    <section className="ppl-print__section ppl-print__section--wide">
      <PrintReportHeader category="Planification" title={t('phrases:p_3ca05e75ef')} year={N} tenant={activeTenant} editedAt={editedAt} />

      {!plan.hasData ? (
        <p className="ppl-print__note">Aucune charge sociale saisie pour {N}.</p>
      ) : (
        <table className="ppl-print__table ppl-print__table--dense ppl-print__table--total-once">
          <caption>Brut planifié × taux · {N} · CHF</caption>
          <thead>
            <tr>
              <th> </th>
              {months.map((m, i) => <th key={i} className="num nowrap">{m.label}</th>)}
              <th className="num nowrap">Total</th>
            </tr>
          </thead>
          <tbody>
            <Row label="Salaires bruts" arr={gross} strong />
            <SecRow label={t('phrases:p_0834336deb')} />
            {charges.filter((c) => c.hasEmpl).map((c) => <Row key={`empl-${c.libelle}`} label={c.libelle} arr={c.empl} />)}
            <Row label={t('phrases:p_e37463cb94')} arr={chargesSalarie} />
            <Row label="= Salaires nets" arr={net} strong />
            <SecRow label="Charges employeur" />
            {charges.filter((c) => c.hasEmpr).map((c) => <Row key={`empr-${c.libelle}`} label={c.libelle} arr={c.empr} />)}
            <Row label="Sous-total charges employeur" arr={chargesEmployeur} />
            <Row label={p('= Coût employeur')} arr={cout} strong />
            <SecRow label={t('phrases:p_7f74d4e2a8')} />
            <Row label="Total charges" arr={sub} strong />
          </tbody>
        </table>
      )}
    </section>
  );
}
